/**
 * 팀 생성/수정/삭제 관련 함수들
 */

import {
  getAuthenticatedUserAndProfile,
  getTeamWithManagerCheck,
  TEAM_MEMBER_STATUS,
  logTeamService,
} from '@/utils/team-helpers'
import { isTeamManager } from '@/utils/team-role'
import { convertSpecialtyToEnum } from '@/utils/team-specialty'
import type { TeamUpdateData } from './team-types'
import type { Database } from '@/types/supabase'

/**
 * 기본 팀 생성
 * 매니저는 만든 유저의 profile_id로 설정
 * @returns 생성된 팀 정보
 */
export const createDefaultTeam = async () => {
  const { supabase, user, profileId } = await getAuthenticatedUserAndProfile()

  // 사용자 이름 조회
  const { data: profile, error: profileError } = await supabase
    .from('accounts')
    .select('username')
    .eq('profile_id', profileId)
    .is('deleted_at', null)
    .maybeSingle()

  if (profileError) {
    throw new Error(`프로필 조회 실패: ${profileError.message}`)
  }

  interface ProfileRow {
    username: string
  }
  const profileTyped = profile as unknown as ProfileRow | null

  // 기본 팀 이름 생성 (사용자 이름 기반)
  const baseTeamName = `${profileTyped?.username || '내'}의 팀`
  let teamName = baseTeamName
  let counter = 1

  // 팀 이름 중복 확인 및 고유 이름 생성
  while (true) {
    const { data: existingTeam } = await supabase
      .from('teams')
      .select('id')
      .eq('name', teamName)
      .eq('manager_profile_id', profileId)
      .is('deleted_at', null)
      .maybeSingle()

    if (!existingTeam) {
      break // 중복되지 않는 이름을 찾음
    }

    counter++
    teamName = `${baseTeamName} ${counter}`
  }

  // 팀 생성 (manager_profile_id에 현재 사용자의 profile_id 설정)
  // Note: as any는 Supabase 타입 정의가 enum 타입을 완벽하게 지원하지 않아 필요
  const { data: newTeam, error: createError } = await supabase
    .from('teams')
    .insert({
      name: teamName,
      manager_profile_id: profileId,
      bio: '', // NOT NULL 제약 조건을 위해 빈 문자열 사용
      specialty: [], // 배열 타입이므로 빈 배열 사용
      sub_specialty: [], // 배열 타입이므로 빈 배열 사용
      prefered: [], // 배열 타입이므로 빈 배열 사용
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    } as any)
    .select()
    .single()

  if (createError) {
    // PostgreSQL unique constraint 위반 에러 코드: 23505
    if (createError.code === '23505') {
      // 중복 이름 자동 생성 재시도 (최대 10회)
      let retryCount = 0
      while (retryCount < 10) {
        counter++
        teamName = `${baseTeamName} ${counter}`
        
        // Note: as any는 Supabase 타입 정의가 enum 타입을 완벽하게 지원하지 않아 필요
        const { data: retryTeam, error: retryError } = await supabase
          .from('teams')
          .insert({
            name: teamName,
            manager_profile_id: profileId,
            manager_id: user.id, // LEGACY 필드 (하위 호환성)
            bio: '',
            specialty: [],
            sub_specialty: [],
            prefered: [],
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          } as any)
          .select()
          .single()
        
        if (!retryError) {
          return { data: retryTeam, error: null }
        }
        
        // unique constraint가 아닌 다른 에러면 즉시 종료
        if (retryError.code !== '23505') {
          throw new Error(`팀 생성 실패: ${retryError.message}`)
        }
        
        retryCount++
      }
      
      throw new Error('팀 이름을 생성하는데 실패했습니다. 다른 이름을 시도해주세요.')
    }
    
    throw new Error(`팀 생성 실패: ${createError.message}`)
  }

  return { data: newTeam, error: null }
}

/**
 * 팀 나가기 요청 (메이커만 가능, 매니저 승인 필요)
 * @param teamId 나갈 팀 ID
 * @deprecated team_member_requests 기반으로 변경됨. createLeaveRequest 사용 권장.
 */
export const requestLeaveTeam = async (teamId: number) => {
  const { createLeaveRequest } = await import('./team-member-requests.service')
  const request = await createLeaveRequest({ teamId })
  return { data: { success: true, message: '나가기 요청이 전송되었습니다. 매니저의 승인을 기다려주세요.', requestId: request.id }, error: null }
}

/**
 * 팀 나가기 (레거시 함수, 호환성을 위해 유지)
 * @deprecated requestLeaveTeam을 사용하세요. 매니저 승인이 필요합니다.
 */
export const leaveTeam = async (teamId: number) => {
  return requestLeaveTeam(teamId)
}

/**
 * 팀 나가기 요청 승인 (매니저만 가능)
 * @param requestId 나가기 요청 ID (team_member_requests.id)
 * @deprecated team_member_requests 기반으로 변경됨. approveLeaveRequest(requestId) 사용 권장.
 */
export const approveLeaveRequest = async (teamId: number, memberProfileId: string) => {
  // 레거시 호환: memberProfileId로 요청 찾기
  const { createBrowserClient } = await import('@/supabase')
  const supabase = createBrowserClient()
  
  const { data: request } = await supabase
    .from('team_member_requests')
    .select('id')
    .eq('team_id', teamId)
    .eq('requester_profile_id', memberProfileId)
    .eq('kind', 'LEAVE')
    .eq('status', 'PENDING')
    .maybeSingle()

  if (!request) {
    throw new Error('나가기 요청을 찾을 수 없습니다.')
  }

  interface RequestRow {
    id: number
  }
  const requestTyped = request as unknown as RequestRow

  const { approveLeaveRequest: approveLeave } = await import('./team-member-requests.service')
  await approveLeave(requestTyped.id)
  return { data: { success: true }, error: null }
}

/**
 * 팀 나가기 요청 거절 (매니저만 가능)
 * @param requestId 나가기 요청 ID (team_member_requests.id)
 * @deprecated team_member_requests 기반으로 변경됨. declineLeaveRequest(requestId) 사용 권장.
 */
export const rejectLeaveRequest = async (teamId: number, memberProfileId: string) => {
  // 레거시 호환: memberProfileId로 요청 찾기
  const { createBrowserClient } = await import('@/supabase')
  const supabase = createBrowserClient()
  
  const { data: request } = await supabase
    .from('team_member_requests')
    .select('id')
    .eq('team_id', teamId)
    .eq('requester_profile_id', memberProfileId)
    .eq('kind', 'LEAVE')
    .eq('status', 'PENDING')
    .maybeSingle()

  if (!request) {
    throw new Error('나가기 요청을 찾을 수 없습니다.')
  }

  interface RequestRow2 {
    id: number
  }
  const requestTyped2 = request as unknown as RequestRow2

  const { declineLeaveRequest: declineLeave } = await import('./team-member-requests.service')
  await declineLeave(requestTyped2.id)
  return { data: { success: true }, error: null }
}

/**
 * 팀 나가기 요청 취소 (요청한 팀원만 가능)
 * @param teamId 팀 ID
 * @deprecated team_member_requests 기반으로 변경됨. cancelLeaveRequest(requestId) 사용 권장.
 */
export const cancelLeaveRequest = async (teamId: number) => {
  const { supabase, profileId } = await getAuthenticatedUserAndProfile()

  // 나가기 요청 조회
  const { data: request } = await supabase
    .from('team_member_requests')
    .select('id')
    .eq('team_id', teamId)
    .eq('requester_profile_id', profileId)
    .eq('kind', 'LEAVE')
    .eq('status', 'PENDING')
    .maybeSingle()

  if (!request) {
    throw new Error('나가기 요청을 찾을 수 없습니다.')
  }

  interface RequestRow3 {
    id: number
  }
  const requestTyped3 = request as unknown as RequestRow3

  const { cancelLeaveRequest: cancelLeave } = await import('./team-member-requests.service')
  await cancelLeave(requestTyped3.id)
  return { data: { success: true }, error: null }
}

/**
 * 팀 제거 (매니저만 가능)
 * @param teamId 제거할 팀 ID
 */
export const deleteTeam = async (teamId: number) => {
  const { supabase: supabaseRaw, profileId } = await getAuthenticatedUserAndProfile()
  const supabase = supabaseRaw as any

  // 팀 정보 조회 및 매니저 확인
  const { team, isManager } = await getTeamWithManagerCheck(supabase, teamId, profileId)

  if (!isManager) {
    throw new Error('팀 매니저만 팀을 삭제할 수 있습니다.')
  }

  // 관련 데이터 처리
  // 1. team_members: 활성 팀원들의 status를 'inactive'로 변경
  const { error: membersError } = await supabase
    .from('team_members')
    .update({
      status: TEAM_MEMBER_STATUS.INACTIVE,
      updated_at: new Date().toISOString(),
    })
    .eq('team_id', teamId)
    .eq('status', TEAM_MEMBER_STATUS.ACTIVE)

  if (membersError) {
    logTeamService('warn', 'deleteTeam', { error: membersError, teamId })
    // 팀원 처리 실패해도 팀 삭제는 진행
  }

  // 2. estimate: 진행 중인 견적서들은 그대로 유지
  // 참고: estimate_status에 'cancelled' 상태가 없으므로, 팀 삭제 시 견적서는 유지됨
  // 필요시 견적서를 별도로 처리해야 함 (예: 수동으로 삭제하거나 상태 변경)

  // 팀 삭제 (soft delete: deleted_at 설정)
  const { error: deleteError } = await supabase
    .from('teams')
    .update({
      deleted_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    })
    .eq('id', teamId)
    .eq('manager_profile_id', profileId)

  if (deleteError) throw deleteError

  return { data: { success: true }, error: null }
}

/**
 * 팀 복구 (매니저만 가능)
 * @param teamId 복구할 팀 ID
 */
export const restoreTeam = async (teamId: number) => {
  const { supabase: supabaseRaw, profileId } = await getAuthenticatedUserAndProfile()
  const supabase = supabaseRaw as any

  // 삭제된 팀 정보 조회
  const { data: team, error: teamError } = await supabase
    .from('teams')
    .select('id, manager_profile_id, deleted_at')
    .eq('id', teamId)
    .not('deleted_at', 'is', null)
    .maybeSingle()

  if (teamError) throw teamError
  if (!team) throw new Error('삭제된 팀을 찾을 수 없습니다.')

  interface TeamRestoreRow {
    id: number
    manager_profile_id: string | null
    deleted_at: string | null
  }
  const teamTyped = team as unknown as TeamRestoreRow

  // 매니저인지 확인
  if (!isTeamManager(teamTyped as any, profileId)) {
    throw new Error('팀 매니저만 팀을 복구할 수 있습니다.')
  }

  // 팀 복구 (deleted_at을 null로 설정)
  const { error: restoreError } = await supabase
    .from('teams')
    .update({
      deleted_at: null,
      updated_at: new Date().toISOString(),
    })
    .eq('id', teamId)
    .eq('manager_profile_id', profileId)

  if (restoreError) throw restoreError

  // 팀원들도 복구 (status를 'active'로 변경)
  const { error: membersError } = await supabase
    .from('team_members')
    .update({
      status: TEAM_MEMBER_STATUS.ACTIVE,
      updated_at: new Date().toISOString(),
    })
    .eq('team_id', teamId)
    .eq('status', TEAM_MEMBER_STATUS.INACTIVE)

  if (membersError) {
    logTeamService('warn', 'restoreTeam', { error: membersError, teamId })
    // 팀원 복구 실패해도 팀 복구는 완료
  }

  return { data: { success: true }, error: null }
}

/**
 * 팀 업데이트 (매니저만 가능)
 * @param teamId 업데이트할 팀 ID
 * @param updateData 업데이트할 데이터
 */
export const updateTeam = async (
  teamId: number,
  updateData: TeamUpdateData
) => {
  const { supabase: supabaseRaw, profileId } = await getAuthenticatedUserAndProfile()
  const supabase = supabaseRaw as any

  // 팀 정보 조회 및 매니저 확인
  const { team, isManager } = await getTeamWithManagerCheck(supabase, teamId, profileId)

  if (!isManager) {
    throw new Error('팀 매니저만 팀 정보를 수정할 수 있습니다.')
  }

  // 팀 정보 업데이트
  type TeamUpdatePayload = Database['public']['Tables']['teams']['Update']
  
  const updatePayload: TeamUpdatePayload = {
    updated_at: new Date().toISOString(),
  }

  // 팀 이름 업데이트 시 중복 체크
  if (updateData.name !== undefined) {
    // 현재 팀 제외하고 중복 체크
    const { data: existingTeam } = await supabase
      .from('teams')
      .select('id')
      .eq('name', updateData.name)
      .eq('manager_profile_id', profileId)
      .neq('id', teamId) // 현재 팀 제외
      .is('deleted_at', null)
      .maybeSingle()

    if (existingTeam) {
      throw new Error('이미 같은 이름의 팀이 존재합니다. 다른 이름을 사용해주세요.')
    }

    updatePayload.name = updateData.name
  }

  if (updateData.bio !== undefined) updatePayload.bio = updateData.bio ?? undefined
  if (updateData.specialty !== undefined) {
    // 영어 코드 값(DESIGN, DATA 등)을 한글 enum 값으로 변환
    const specialtyArray = Array.isArray(updateData.specialty) ? updateData.specialty : []
    const convertedSpecialty = specialtyArray.length > 0 
      ? convertSpecialtyToEnum(specialtyArray)
      : undefined
    updatePayload.specialty = (convertedSpecialty ?? undefined) as Database['public']['Enums']['team_specialty'][] | undefined
  }
  if (updateData.sub_specialty !== undefined) updatePayload.sub_specialty = updateData.sub_specialty ?? undefined
  if (updateData.prefered !== undefined) updatePayload.prefered = updateData.prefered ?? undefined
  if (updateData.preferred_project_period !== undefined) updatePayload.preferred_project_period = (updateData.preferred_project_period ?? undefined) as Database['public']['Enums']['counsel_period'] | null | undefined
  if (updateData.preferred_contract_amount !== undefined) updatePayload.preferred_contract_amount = (updateData.preferred_contract_amount ?? undefined) as Database['public']['Enums']['counsel_cost'] | null | undefined

  const { data: updatedTeam, error: updateError } = await supabase
    .from('teams')
    .update(updatePayload)
    .eq('id', teamId)
    .eq('manager_profile_id', profileId)
    .select()
    .single()

  if (updateError) {
    // Unique constraint 위반 처리 (PostgreSQL 에러 코드: 23505)
    if (updateError.code === '23505') {
      throw new Error('이미 같은 이름의 팀이 존재합니다. 다른 이름을 사용해주세요.')
    }
    throw updateError
  }

  return { data: updatedTeam, error: null }
}

/**
 * 팀원 제거 (매니저만 가능)
 * @param teamId 팀 ID
 * @param memberProfileId 제거할 팀원의 profile_id
 */
export const removeTeamMember = async (teamId: number, memberProfileId: string) => {
  const { supabase, profileId } = await getAuthenticatedUserAndProfile()

  // 팀 정보 조회 및 매니저 확인
  const { team, isManager } = await getTeamWithManagerCheck(supabase as any, teamId, profileId)

  if (!isManager) {
    throw new Error('팀 매니저만 팀원을 제거할 수 있습니다.')
  }

  // 매니저 자신을 제거하려는 경우 방지
  if (memberProfileId === profileId) {
    throw new Error('자기 자신을 제거할 수 없습니다. 팀을 삭제하거나 다른 매니저에게 권한을 넘겨주세요.')
  }

  // 팀원 정보 조회
  const { data: member, error: memberError } = await supabase
    .from('team_members')
    .select('id')
    .eq('team_id', teamId)
    .eq('profile_id', memberProfileId)
    .maybeSingle()

  if (memberError) throw memberError
  if (!member) {
    throw new Error('팀원 정보를 찾을 수 없습니다.')
  }

  interface MemberDeleteRow {
    id: number
  }
  const memberTyped = member as unknown as MemberDeleteRow

  // team_members의 status를 inactive로 변경 (soft delete)
  const { error: updateError } = await supabase
    .from('team_members')
    .update({
      status: 'inactive',
      updated_at: new Date().toISOString(),
    })
    .eq('id', memberTyped.id)
    .eq('status', 'active') // active 상태인 것만 업데이트

  if (updateError) throw updateError

  return { data: { success: true }, error: null }
}

/**
 * 팀 합류 신청 (메이커가 팀에 합류 신청)
 * @param teamId 팀 ID
 * @deprecated team_member_requests 기반으로 변경됨. createJoinRequest() 사용 권장
 */
export const requestTeamJoin = async (teamId: number) => {
  const { createJoinRequest } = await import('./team-member-requests.service')
  
  try {
    const result = await createJoinRequest({
      teamId,
      message: null,
          })
    
    return { 
      data: { 
        success: true, 
        message: '합류 신청이 완료되었습니다. 매니저의 승인을 기다려주세요.',
        requestId: result.id 
      }, 
      error: null 
    }
  } catch (error: any) {
    return { 
      data: null, 
      error: new Error(error.message || '합류 신청에 실패했습니다.') 
    }
  }
}

